import { useState, useEffect } from "react";
import to from "await-to-js";
import { DialogContent, DialogTitle } from "@mui/material";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { CopyToClipboard } from "react-copy-to-clipboard";

import UKDialog from "@/common-components-src/components/UKDialog/index";
import UKToast from "@/common-components-src/components/UKToast/index";
import UKLoadingIcon from "@/common-components-src/components/UKLoadingIcon";
import CouponCard from "@/app/csr-src/pages/profile/voucher/components/couponCard/index";

import cn from "clsx";
import EmptyPage from "../emptyPage";
import RefundTipsModal from "../refundTipsModal";
import styles from "./index.module.scss";
import { orderCancelRefund } from "@/app/csr-src/apis/order/order.api";
import { IOrderDetailReq, RefundDetailInfoDTO } from "@/app/csr-src/apis/order/order.interface";
import  TipIcon  from "@/app/csr-src/assets/icons/TipIcon.svg";
import  VectorDown  from "@/app/csr-src/assets/icons/VectorDown.svg";

interface refundModalProps {
  orderId: string;
  isOpen: boolean;
  closeHandler: () => void;
  handleConfirmClick: () => void;
}

const refundModal: React.FC<refundModalProps> = observer(
  ({ orderId, isOpen, closeHandler, handleConfirmClick }) => {
    const { t } = useTranslation();

    const [refundDetailData, setRefundDetailData] = useState<RefundDetailInfoDTO | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const [showRefundTipsModal, setShowRefundTipsModal] = useState(false);
    const [isItemViisible, setIsItemViisible] = useState(false);

    const loadRefundDetailData = async () => {
      const requestData: IOrderDetailReq = { orderId: String(orderId) };
      const [err, cancelRefundData] = await to(orderCancelRefund(requestData));

      if (err) {
        setError("Failed to fetch order details");
        setRefundDetailData(null);
      } else {
        setRefundDetailData(cancelRefundData as RefundDetailInfoDTO);
        setError(null);
      }

      setLoading(false);
    };

    useEffect(() => {
      loadRefundDetailData();
    }, []);

    // 复制售后单号
    const copyHandler = () => {
      UKToast.show({
        content: t("order.orderDetail.common.copySuccessTips"), // 弹窗内容
        icon: "success", // 可选：success, fail, loading, warn, info，影响图标
        duration: 2000, // 不写的话默认3000
      });
    };

    // 展开与收起功能
    const toggleExpand = () => {
      setIsItemViisible(!isItemViisible);
    };

    return (
      <>
        <UKDialog
          open={isOpen}
          onClose={closeHandler}
          showCloseBtn={true}
          className={styles.refund_modal}
        >
          <DialogTitle>{t("order.orderDetail.refundModal.modalTitle")}</DialogTitle>
          <DialogContent>
            {loading ? (
              <div className={styles.loading_wrap}>
                <UKLoadingIcon type="black" />
                <span className={styles.loading_text}>{t("common.pageStatus.loading")}</span>
              </div>
            ) : refundDetailData ? (
              <div className={styles.refund_wrap}>
                <div className={styles.content_wrap}>
                  <div className={styles.refund_item_title}>
                    <div>{refundDetailData?.refundAmountTitle}</div>
                    <TipIcon
                      className={styles.tip_ico}
                      onClick={() => {
                        setShowRefundTipsModal(true);
                      }}
                    />
                  </div>
                  <div className={styles.refund_item_title}>
                    <div className={styles.refund_item_title_left}>Refund amount</div>
                    <div className={styles.price_wrap}>{refundDetailData?.refundAmount}</div>
                  </div>
                  {!!refundDetailData?.couponInfos?.length && (
                    <div className={styles.refund_item_title}>
                      <div className={styles.refund_item_title_left}>Returned coupon(s)</div>
                      <div onClick={toggleExpand} className={styles.expand_wrap}>
                        <VectorDown
                          className={cn(styles.more_ico, {
                            [styles.retract_ico]: isItemViisible,
                          })}
                        />
                      </div>
                    </div>
                  )}
                  {isItemViisible && !!refundDetailData?.couponInfos?.length && (
                    <div>
                      <CouponCard
                        data={refundDetailData?.couponInfos}
                        currentTab="available"
                        containerStyle={{ width: "100%", marginBottom: "8px" }}
                        contentStyle={{ width: "375px" }}
                        isShowUseBtn={false}
                      />
                    </div>
                  )}
                </div>
                {!!refundDetailData?.items?.length && (
                  <div className={styles.base_info_wrap}>
                    {refundDetailData?.items.map((item, index) => {
                      return (
                        <div className={styles.base_info_item} key={index}>
                          <div>{item?.label}</div>
                          <div>
                            {item?.value}
                            {item?.showCopy && (
                              <CopyToClipboard text={item?.value} onCopy={() => copyHandler()}>
                                <span className={styles.copy_btn}>
                                  {t("order.orderDetail.common.copyText")}
                                </span>
                              </CopyToClipboard>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <EmptyPage />
            )}
          </DialogContent>
        </UKDialog>
        {showRefundTipsModal && (
          <RefundTipsModal
            isOpen={showRefundTipsModal}
            content={refundDetailData?.refundAmountTips}
            closeHandler={() => setShowRefundTipsModal(false)}
          />
        )}
      </>
    );
  },
);

export default refundModal;
